import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Building2, Users, Link as LinkIcon, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default CMS content (matches admin editor defaults)
const defaultContent = {
  hero: {
    title: 'Our',
    highlight: 'Affiliates',
    description: 'Partner associations working together to strengthen the voice of public employees across Papua New Guinea',
  },
  intro: {
    heading: 'Stronger Together',
    body_markdown: 'PEA PNG is proud to work alongside specialized associations that represent specific sectors within the public service. Our affiliates share our commitment to advocating for better working conditions, professional development, and fair treatment for all public employees across Papua New Guinea.',
  },
  benefits: {
    heading: 'Partnership Benefits',
    items: [
      'Collective Strength - Unified voice in advocating for public employee rights',
      'Shared Resources - Access to legal services, training programs, and organizational support',
      'Network Growth - Expanded networking opportunities and knowledge sharing',
    ],
  },
  cta: {
    heading: 'Interested in Becoming an Affiliate?',
    body_markdown: 'Join us in strengthening the public service sector across Papua New Guinea',
    button_text: 'Contact Us',
    button_link: '/contact',
  },
};

const AffiliatesPage = () => {
  const [loading, setLoading] = useState(true);
  const [affiliates, setAffiliates] = useState([
    {
      id: 1,
      name: 'Health Extensions Officers Association',
      description: 'Representing health extension officers across Papua New Guinea, advocating for professional development and better working conditions in the healthcare sector.',
      established: '2020',
      members: '500+',
      contact: 'heoa@peapng.org.pg'
    },
    {
      id: 2,
      name: 'Medical Laboratory Technical Personnel Services (MLTPS) Association',
      description: 'Dedicated to supporting medical laboratory technical personnel throughout PNG, ensuring high standards in laboratory services and professional recognition.',
      established: '2019',
      members: '350+',
      contact: 'mltps@peapng.org.pg'
    }
  ]);
  const [content, setContent] = useState(defaultContent);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [affiliatesRes, contentRes] = await Promise.all([
        axios.get(`${API}/affiliates`),
        axios.get(`${API}/page-content/affiliates`)
      ]);
      
      if (affiliatesRes.data && affiliatesRes.data.length > 0) {
        setAffiliates(affiliatesRes.data);
      }
      
      // Set page content from CMS
      const next = { ...defaultContent };
      contentRes.data.forEach(item => {
        if (item.section_name && item.content) {
          next[item.section_name] = { ...next[item.section_name], ...item.content };
        }
      });
      setContent(next);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section - CMS Driven */}
      <section className="bg-gradient-to-br from-orange-50 via-blue-50 to-green-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            {content.hero.title} <span className="text-orange-500">{content.hero.highlight}</span>
          </h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            {content.hero.description}
          </p>
        </div>
      </section>

      {/* Introduction Section - CMS Driven */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-12">
            <Building2 className="mx-auto text-orange-500 mb-6" size={64} />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{content.intro.heading}</h2>
            <p className="text-gray-700 leading-relaxed">
              {content.intro.body_markdown}
            </p>
          </div>
        </div>
      </section>

      {/* Affiliates List */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Current Affiliates</h2>
            <p className="text-gray-600">
              Meet our partner associations
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="space-y-8">
              {affiliates.map((affiliate) => (
                <div
                  key={affiliate.id}
                  data-testid={`affiliate-${affiliate.id}`}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden card-hover"
                >
                  <div className="md:flex">
                    {/* Left Side - Icon/Logo Area */}
                    <div className="md:w-1/3 bg-gradient-to-br from-orange-400 to-orange-600 p-8 flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-24 h-24 bg-white bg-opacity-20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl">
                          <Users className="text-white" size={48} />
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">{affiliate.name}</h3>
                      </div>
                    </div>

                    {/* Right Side - Content */}
                    <div className="md:w-2/3 p-8">
                      <p className="text-gray-700 leading-relaxed mb-6">
                        {affiliate.description}
                      </p>

                      <div className="grid md:grid-cols-3 gap-4 mb-6">
                        <div className="bg-orange-50 rounded-lg p-4 text-center">
                          <p className="text-sm text-gray-600 mb-1">Established</p>
                          <p className="text-xl font-bold text-orange-600">{affiliate.established}</p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-4 text-center">
                          <p className="text-sm text-gray-600 mb-1">Members</p>
                          <p className="text-xl font-bold text-blue-600">{affiliate.members}</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4 text-center">
                          <p className="text-sm text-gray-600 mb-1">Status</p>
                          <p className="text-xl font-bold text-green-600">Active</p>
                        </div>
                      </div>

                      {affiliate.contact && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <LinkIcon size={18} className="text-orange-500" />
                          <span className="text-sm">Contact: {affiliate.contact}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Partnership Benefits - CMS Driven */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">{content.benefits.heading}</h2>
            <p className="text-gray-600">
              What our affiliates gain through partnership
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {(content.benefits.items || []).map((benefit, index) => {
              const icons = [Users, Building2, LinkIcon];
              const colors = [
                { bg: 'from-orange-50 to-orange-100', icon: 'bg-orange-500' },
                { bg: 'from-blue-50 to-blue-100', icon: 'bg-blue-400' },
                { bg: 'from-green-50 to-green-100', icon: 'bg-green-400' }
              ];
              const Icon = icons[index] || Users;
              const color = colors[index] || colors[0];
              
              // Parse benefit text (may be "Title - Description" format)
              const parts = benefit.split(' - ');
              const title = parts[0] || benefit;
              const description = parts[1] || '';
              
              return (
                <div key={index} className={`text-center p-8 bg-gradient-to-br ${color.bg} rounded-2xl card-hover`}>
                  <div className={`w-16 h-16 ${color.icon} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                    <Icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
                  {description && (
                    <p className="text-gray-700 text-sm">{description}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section - CMS Driven */}
      <section className="py-20 bg-gradient-to-r from-blue-500 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            {content.cta.heading}
          </h2>
          <p className="text-lg text-blue-100 mb-8">
            {content.cta.body_markdown}
          </p>
          <Link
            to={content.cta.button_link || '/contact'}
            className="inline-flex items-center px-8 py-4 bg-white text-blue-600 rounded-xl font-bold hover:bg-blue-50 transition-all hover:scale-105 shadow-xl"
          >
            {content.cta.button_text || 'Contact Us'}
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AffiliatesPage;
